import { useCallback, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';
import type { FocusGroupResponseDto } from '@inference.engine/persona-api-client';
import { Button, Spinner } from '@monksflow/monks-ui';
import { CheckIcon, FolderPlusIcon, PanelLeftIcon, PlusIcon } from 'lucide-react';
import { usePermissions } from '@/entities/user';
import { getWorkspaceId, useCurrentWorkspaceId } from '@/entities/workspace';
import { notificationService } from '@/lib/services/notification.service';
import { logger } from '@/lib/utils/logger';
import { downloadFile, sanitizeDownloadFileName } from '@/shared/utils/downloadFile';
import { track } from '@/shared/analytics';
import { DeepLinkInterstitial, DownloadIconButton } from '@/shared/ui';
import { WizardLayout } from '@/shared/ui/layout';
import { ROUTES } from '@/shared/config/routes';
import {
  useDeepLinkResolution,
  useSaveToProjectEnabled,
  isTerminalResolution,
} from '@/shared/hooks';
import { SaveToProjectPopover } from '@/shared/ui';
import { readFocusGroupsOrigin, withFocusGroupsOrigin } from '@/shared/utils/focusGroupsNavigation';
import { focusGroupResolutionQuery, saveFocusGroupAsArtifact } from '../../api';
import { downloadFocusGroupPdf } from '../../api';
import { useFocusGroup } from '../../api/useFocusGroup';
import { FocusGroupReportContent } from './FocusGroupReportContent';
import { buildStepLabels } from '../../constants';
import { useTranslation } from 'react-i18next';

export function FocusGroupReport() {
  const { t } = useTranslation('audiences');

  const { focusGroupId } = useParams<{ focusGroupId: string }>();
  const navigate = useNavigate();
  const { state } = useLocation() as { state: unknown };
  // Preserve the section entry origin so the back button keeps resolving to it.
  const origin = readFocusGroupsOrigin(state);
  const { canAccessFocusGroups } = usePermissions();

  // Deep-link seam: resolve the id's owning workspace first and
  // hold the focus-group fetch until resolution settles — under the wrong
  // workspace header it would 404 into the error branch prematurely.
  const resolution = useDeepLinkResolution({
    resolutionQuery: focusGroupResolutionQuery(focusGroupId),
    enabled: !!focusGroupId,
  });

  const {
    data: focusGroup,
    isLoading,
    isError,
  } = useFocusGroup(focusGroupId, {
    // The seam's interstitial owns the not-found UX, not the global toast.
    suppressErrorNotification: true,
    enabled: resolution.status === 'ready',
  });
  const [showPanel, setShowPanel] = useState(true);
  const workspaceId = useCurrentWorkspaceId();

  if (isLoading || resolution.status === 'resolving') {
    return (
      <div className="flex items-center justify-center h-full">
        <Spinner size="lg" />
      </div>
    );
  }

  if (isTerminalResolution(resolution)) {
    return <DeepLinkInterstitial state={resolution} entity="focusGroup" />;
  }

  if (isError || !focusGroup) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-muted-foreground">
          {t('focusGroup.focusGroupReport.focusGroupNotFound')}
        </p>
      </div>
    );
  }

  const stepLabels = buildStepLabels(t);

  return (
    <WizardLayout
      configTitle={t('focusGroup.focusGroupReport.focusGroupConfiguration')}
      steps={stepLabels}
      activeStep={stepLabels.length - 1}
      showConfigPanel={showPanel}
      onBack={() => void navigate(ROUTES.TOOLS_FOCUS_GROUPS, withFocusGroupsOrigin(origin))}
      configContent={
        <div className="flex flex-col items-center justify-center flex-1 gap-6">
          <div className="flex items-center justify-center w-12 h-12 rounded bg-green-100 dark:bg-positive">
            <CheckIcon size={24} className="text-green-600 dark:text-positive-foreground" />
          </div>
          <p className="text-xl font-medium text-foreground tracking-tight">
            {t('focusGroup.focusGroupReport.yourReportIsReady')}
          </p>
          {canAccessFocusGroups && (
            <Button
              variant="outline"
              onClick={() =>
                void navigate(ROUTES.TOOLS_FOCUS_GROUPS_NEW, withFocusGroupsOrigin(origin))
              }
            >
              {t('focusGroup.focusGroupReport.newFocusGroup')}
              <PlusIcon size={16} />
            </Button>
          )}
        </div>
      }
    >
      <div className="flex flex-1 flex-col overflow-hidden font-default">
        <ReportHeader
          focusGroup={focusGroup}
          workspaceId={workspaceId}
          onTogglePanel={() => setShowPanel((v) => !v)}
        />
        <FocusGroupReportContent focusGroup={focusGroup} />
      </div>
    </WizardLayout>
  );
}

function ReportHeader({
  focusGroup,
  workspaceId,
  onTogglePanel,
}: {
  focusGroup: FocusGroupResponseDto;
  workspaceId: string | null;
  onTogglePanel: () => void;
}) {
  const { t } = useTranslation('audiences');

  const { canAccessFocusGroups } = usePermissions();
  const saveToProjectEnabled = useSaveToProjectEnabled();

  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownload = useCallback(async () => {
    setIsDownloading(true);
    const notifId = notificationService.showInfo(
      t('focusGroup.focusGroupReport.preparingYourDownloadThisMay'),
      { persist: true },
    );

    try {
      const workspaceId = getWorkspaceId() ?? '';
      const base = sanitizeDownloadFileName(focusGroup.title ?? '', 'focus-group');
      // Interviews transcripts exceed Chromium's single-page PDF height cap,
      // so the report is exported as two files: summary, then interviews.
      // eslint-disable-next-line i18next/no-literal-string -- report section wire enum values
      for (const section of ['summary', 'interviews'] as const) {
        const blob = await downloadFocusGroupPdf(focusGroup.id, workspaceId, section);
        await downloadFile({
          blob,
          mimeType: 'application/pdf',
          fileName: `${base} (${section}).pdf`,
        });
      }
      track('document_downloaded', {
        document_type: 'focus_group',
        format: 'pdf',
        downloader: 'loc',
      });
    } catch (err) {
      logger.error('Failed to download focus-group report', err);
      notificationService.showError(t('focusGroup.focusGroupReport.failedToDownloadReport'));
    } finally {
      notificationService.close(notifId);
      setIsDownloading(false);
    }
  }, [focusGroup.id, focusGroup.title, t]);

  return (
    <div className="flex items-center justify-between px-6 py-3 border-b border-border min-h-16">
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          size="mini"
          className="h-8 w-8 p-0"
          aria-label={t('focusGroup.focusGroupReport.toggleConfigurationPanel')}
          onClick={onTogglePanel}
        >
          <PanelLeftIcon size={16} />
        </Button>
        <h1 className="text-base font-medium text-foreground">{focusGroup.title}</h1>
      </div>
      <div className="flex items-center gap-2">
        {canAccessFocusGroups && (
          <DownloadIconButton
            label={t('focusGroup.focusGroupReport.downloadReport')}
            onDownload={handleDownload}
            isDownloading={isDownloading}
          />
        )}
        {canAccessFocusGroups && saveToProjectEnabled && workspaceId && (
          <SaveToProjectPopover
            resourceId={focusGroup.id}
            workspaceId={workspaceId}
            onMintArtifact={() => saveFocusGroupAsArtifact(focusGroup.id, workspaceId)}
          >
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label={t('focusGroup.focusGroupReport.saveToProject')}
            >
              <FolderPlusIcon className="size-5" />
            </Button>
          </SaveToProjectPopover>
        )}
      </div>
    </div>
  );
}
